import { Suspense, lazy, useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import { GlobeView } from "./features/globe/GlobeView";
import { AppShell } from "./shell/AppShell";
import { RouteLoader } from "./shell/RouteLoader";
import { Splash } from "./shell/Splash";

// The globe is the front door — keep it eager.
// Every other route is code-split so the initial JS payload stays small.
const AirQualityRoute = lazy(() =>
  import("./features/air-quality/AirQualityRoute").then((m) => ({ default: m.AirQualityRoute })),
);
const PreparednessRoute = lazy(() =>
  import("./features/preparedness/PreparednessRoute").then((m) => ({
    default: m.PreparednessRoute,
  })),
);
const SharedView = lazy(() =>
  import("./features/preparedness/SharedView").then((m) => ({ default: m.SharedView })),
);
const ClimateRoute = lazy(() =>
  import("./features/climate/ClimateRoute").then((m) => ({ default: m.ClimateRoute })),
);
const AboutView = lazy(() =>
  import("./features/about/AboutView").then((m) => ({ default: m.AboutView })),
);
// The owner's panel. Lazy like the rest, and rendered outside AppShell — see
// the branch in App() for why.
const AdminRoute = lazy(() =>
  import("./features/admin/AdminRoute").then((m) => ({ default: m.AdminRoute })),
);

export function App() {
  const location = useLocation();
  const [splashDone, setSplashDone] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ms = reduce ? 400 : 1400;
    const t = setTimeout(() => setSplashDone(true), ms);
    return () => clearTimeout(t);
  }, []);

  // After the splash, idle-prefetch the heaviest non-globe routes so
  // navigating to them feels instant. The dynamic imports are no-ops if
  // the chunks are already loaded.
  useEffect(() => {
    if (!splashDone) return;
    const ric =
      (window as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback ??
      ((cb: () => void) => window.setTimeout(cb, 1500));
    ric(() => {
      void import("./features/air-quality/AirQualityRoute");
      void import("./features/climate/ClimateRoute");
      void import("./features/preparedness/PreparednessRoute");
    });
  }, [splashDone]);

  // The panel is its own surface: no map, no navigation rail, no assistant.
  // Inside AppShell it would mount Cesium and the whole chrome for a page that
  // is four forms, and it would put the splash animation in front of the owner
  // every time they came to fix a key.
  if (location.pathname.startsWith("/admin")) {
    return (
      <Suspense fallback={<RouteLoader label="Loading admin" />}>
        <AdminRoute />
      </Suspense>
    );
  }

  return (
    <>
      {!splashDone && <Splash />}
      <AppShell>
        <Routes>
          <Route path="/" element={<GlobeView />} />
          <Route
            path="/air-quality"
            element={
              <Suspense fallback={<RouteLoader label="Loading air quality" />}>
                <AirQualityRoute />
              </Suspense>
            }
          />
          <Route
            path="/preparedness"
            element={
              <Suspense fallback={<RouteLoader label="Loading preparedness hub" />}>
                <PreparednessRoute />
              </Suspense>
            }
          />
          <Route
            path="/preparedness/shared"
            element={
              <Suspense fallback={<RouteLoader label="Loading shared progress" />}>
                <SharedView />
              </Suspense>
            }
          />
          <Route
            path="/climate"
            element={
              <Suspense fallback={<RouteLoader label="Loading climate trends" />}>
                <ClimateRoute />
              </Suspense>
            }
          />
          <Route
            path="/about"
            element={
              <Suspense fallback={<RouteLoader label="Loading about" />}>
                <AboutView />
              </Suspense>
            }
          />
        </Routes>
      </AppShell>
    </>
  );
}
